import { useEffect, useState } from "react";
import { get, post } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { BarChart3, ShieldCheck, Ban, FileCheck2, Lock } from "lucide-react";

export function ReportBuilder() {
  const [cfg, setCfg] = useState(null);
  const [f, setF] = useState({ template_id: "experience_reach", activity: "All activities", period: "All periods" });
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { get("/reports/config").then(setCfg); }, []);

  const generate = async () => {
    setBusy(true);
    try { const r = await post("/reports/generate", f); setRun(r); toast.success("Report generated."); }
    catch { toast.error("Could not generate report."); }
    setBusy(false);
  };
  if (!cfg) return null;

  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-6" data-testid="report-builder">
      <div className="flex items-center gap-2"><BarChart3 className="h-5 w-5 text-mill-moss" /><h3 className="font-display text-lg font-semibold text-mill-ink">Create a report</h3></div>
      <p className="mt-1 text-sm text-mill-muted">Choose an approved purpose and scope. You cannot inspect underlying fields or individual answers — only policy-bound aggregate output is returned.</p>

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div><Label className="text-xs text-mill-muted">Approved purpose</Label>
          <select className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm" value={f.template_id} onChange={(e) => setF({ ...f, template_id: e.target.value })} data-testid="rpt-template">
            {cfg.templates.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
          </select>
        </div>
        <div><Label className="text-xs text-mill-muted">Activity scope</Label>
          <select className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm" value={f.activity} onChange={(e) => setF({ ...f, activity: e.target.value })} data-testid="rpt-activity">
            {cfg.activities.map((a) => <option key={a}>{a}</option>)}
          </select>
        </div>
        <div><Label className="text-xs text-mill-muted">Reporting period</Label>
          <select className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm" value={f.period} onChange={(e) => setF({ ...f, period: e.target.value })} data-testid="rpt-period">
            {cfg.periods.map((p) => <option key={p}>{p}</option>)}
          </select>
        </div>
      </div>
      <Button className="mt-4 bg-mill-moss hover:bg-mill-moss-dark" onClick={generate} disabled={busy} data-testid="rpt-generate">Generate report</Button>

      <p className="mt-3 flex items-center gap-1.5 text-xs text-mill-muted"><Lock className="h-3.5 w-3.5" /> Privacy: general metrics min {cfg.privacy.general_min}; protected-characteristic metrics suppressed below {cfg.privacy.sensitive_min}. No individual drill-down.</p>

      {run && <ReportOutput run={run} />}
    </div>
  );
}

function CountRow({ label, data }) {
  if (!data) return null;
  return (
    <div className="mt-3">
      <p className="text-xs font-semibold uppercase tracking-wider text-mill-muted">{label}</p>
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {Object.entries(data.shown).map(([k, v]) => <span key={k} className="rounded-full bg-mill-alt px-2.5 py-1 text-xs text-mill-ink">{k}: {v}</span>)}
        {Object.keys(data.shown).length === 0 && <span className="text-xs text-mill-muted">No categories above threshold.</span>}
        {data.suppressed.map((s) => <span key={s} className="rounded-full bg-stone-100 px-2.5 py-1 text-xs text-stone-500" title={`Suppressed — below ${data.threshold}`}>{s}: suppressed</span>)}
      </div>
    </div>
  );
}

function ReportOutput({ run }) {
  const o = run.output;
  const m = run.manifest;
  return (
    <div className="mt-5 space-y-4" data-testid="report-output">
      <div className="rounded-2xl border border-stone-200 bg-mill-alt/30 p-5">
        <h4 className="font-display text-base font-semibold text-mill-ink">{run.template_title}</h4>
        {o.suppressed ? (
          <div className="mt-3 flex items-center gap-2 rounded-xl bg-[#FEF2F2] p-4 text-sm text-[#991B1B]" data-testid="report-suppressed">
            <Ban className="h-4 w-4" /> {o.message} (cohort {o.cohort}, need {o.required}).
          </div>
        ) : (
          <div className="mt-3 space-y-1 text-sm">
            {"total_evaluations" in o && <Stat label="Total completed evaluations" v={o.total_evaluations} />}
            {"participation_volume" in o && <Stat label="Participation volume" v={o.participation_volume} />}
            {"first_visit_pct" in o && <Stat label="First-visit rate" v={`${o.first_visit_pct}%`} />}
            {"first_time_pct" in o && <Stat label="First-time participation" v={`${o.first_time_pct}%`} />}
            {"avg_enjoyment" in o && <Stat label="Average enjoyment (1–5)" v={o.avg_enjoyment} />}
            {"satisfaction_positive_pct" in o && <Stat label="Rated 4–5 (positive)" v={`${o.satisfaction_positive_pct}%`} />}
            {"completion_rate_pct" in o && <Stat label="Response completion rate" v={`${o.completion_rate_pct}%`} />}
            {"equality_completion_rate_pct" in o && <Stat label="Equality-data completion rate" v={`${o.equality_completion_rate_pct}%`} />}
            <CountRow label="How people heard about it" data={o.heard_about} />
            <CountRow label="Age band" data={o.age_band} />
            <CountRow label="Gender" data={o.gender} />
            <CountRow label="Ethnicity" data={o.ethnicity} />
            <CountRow label="Disability / long-term condition" data={o.disability} />
            <CountRow label="Postcode area" data={o.postcode_area} />
            <CountRow label="Feedback themes (pre-classified)" data={o.feedback_themes} />
            {o.note && <p className="mt-2 text-xs text-mill-muted">{o.note}</p>}
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-dashed border-stone-300 p-5" data-testid="report-manifest">
        <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-mill-moss" /><h4 className="font-display text-sm font-semibold text-mill-ink">How this report was created</h4></div>
        <dl className="mt-3 grid grid-cols-1 gap-2 text-xs sm:grid-cols-2">
          <M label="Report purpose" v={m.purpose} />
          <M label="Data source" v={m.data_source} />
          <M label="Reporting period" v={m.reporting_period} />
          <M label="Activity scope" v={m.activity_scope} />
          <M label="Consent basis" v={m.consent_basis} />
          <M label="Cohort" v={`${m.cohort} eligible synthetic responses`} />
          <M label="Privacy rule" v={m.privacy_rule} />
          <M label="Status" v={m.status} />
        </dl>
      </div>

      <p className="flex items-center gap-1.5 rounded-lg bg-[#FFFDF5] px-3 py-2 text-xs text-[#B45309]" data-testid="report-evidence"><FileCheck2 className="h-3.5 w-3.5" /> {run.evidence.text} <span className="text-[10px] text-mill-muted">· {run.evidence.verification}</span></p>
    </div>
  );
}

function Stat({ label, v }) { return <div className="flex items-center justify-between border-b border-stone-100 py-1.5"><span className="text-mill-muted">{label}</span><span className="font-semibold text-mill-ink">{v}</span></div>; }
function M({ label, v }) { return <div><dt className="text-[10px] uppercase tracking-wider text-mill-muted">{label}</dt><dd className="text-mill-ink">{v}</dd></div>; }
